import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ChatSession, GenerationConfig, GenerativeModel, GoogleGenerativeAI, SchemaType } from '@google/generative-ai';
import { marked } from 'marked';
import { BehaviorSubject, Observable } from 'rxjs';
import { AddHistoryService } from 'src/portal/ai-proxy-api/services/add-history.service';
import { INSTRUCTIONS } from './instructions.constants';

export interface ChatMessage {
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  isTyping?: boolean;
  companies?: CompanyDomain[];
}

export interface CompanyDomain {
  company_name: string;
  domain: string;
}

export enum ChatCommand {
  FindCustomers = 'Find customers of products',
  AnalyzeTechStacks = 'Analyze tech stacks for companies',
  RecommendSoftware = 'Recommend software',
  BenchmarkCompanies = 'Benchmark companies',
  GeneralMode = 'General Mode'
}

interface CompanyDomainsData {
  company_domains?: unknown;
  [key: string]: unknown;
}

interface ModelConfig {
  systemInstruction: string;
  temperature?: number;
}

@Injectable({ providedIn: 'root' })
export class AiChatService {
  private readonly apiKey = 'AIzaSyCEQ-DkCwkA3ksRpzaNsgB8m1eEfkLADhA';
  private genAI: GoogleGenerativeAI;
  private model: GenerativeModel;
  private chatSession: ChatSession;

  private chatHistorySubject = new BehaviorSubject<ChatMessage[]>([]);
  public chatHistory$: Observable<ChatMessage[]> = this.chatHistorySubject.asObservable();
  private chatHistory: ChatMessage[] = [];

  public hiFlag = false;
  public selectedMode: ChatCommand = ChatCommand.GeneralMode;
  public companyDomains: CompanyDomain[] = [];
  public isLoading = false;

  constructor(private router: Router,private history:AddHistoryService) {
    this.genAI = new GoogleGenerativeAI(this.apiKey);
    this.initializeModel({
      systemInstruction: INSTRUCTIONS.systemInstructionMain,
    });
    this.setupMarkedRenderer();
  }

  private setupMarkedRenderer(): void {
    const renderer = new marked.Renderer();

    // Override code block rendering
    renderer.code = ({ text, lang }) => {
      if (lang === 'json') {
        try {
          const parsedJson = JSON.parse(text);
          const formattedJson = JSON.stringify(parsedJson, null, 2);
          return `
                  <details class="mt-2 mb-2">
                  <summary><b>Expand / Collapse JSON</b></summary>
                  <pre><code class="language-json">${formattedJson}</code></pre>
                  </details>`;
        } catch (e) {
          return `<pre><code>${text}</code></pre>`;
        }
      }
      return `${text}`;
    };
    marked.setOptions({ renderer });
  }

  private updateChatHistory(): void {
    this.chatHistorySubject.next([...this.chatHistory]);
  }

  private addMessage(message: ChatMessage): void {
    this.chatHistory.push(message);
    this.updateChatHistory();
  }

  private initializeModel(config: ModelConfig): void {
    this.model = this.genAI.getGenerativeModel({
      model: 'gemini-2.0-flash',
      //model: "gemini-2.5-pro-exp-03-25",
      systemInstruction: config.systemInstruction,
    });

    this.chatSession = this.model.startChat({
      generationConfig: {
        temperature: config.temperature ?? 1,
        topP: 0.95,
        topK: 40,
        maxOutputTokens: 8192,
        responseMimeType: 'text/plain',
      },
      history: [],
    });
  }

  private configureMode(message: string): string {
    const modeConfigs: Record<ChatCommand, ModelConfig> = {
      [ChatCommand.FindCustomers]: {
        systemInstruction: INSTRUCTIONS.systemInstructionCompaniesUseProduct,
        temperature: 0.7,
      },
      [ChatCommand.AnalyzeTechStacks]: {
        systemInstruction:
          INSTRUCTIONS.systemInstructionCompanySoftwareAnalysis,
        temperature: 0.7,
      },
      [ChatCommand.RecommendSoftware]: {
        systemInstruction: INSTRUCTIONS.systemInstructionMain,
      },
      [ChatCommand.BenchmarkCompanies]: {
        systemInstruction: INSTRUCTIONS.systemInstructionMain,
      },
      [ChatCommand.GeneralMode]: {
        systemInstruction: INSTRUCTIONS.systemInstructionMain,
      },
    };

    const command = message as ChatCommand;
    if (modeConfigs[command]) {
      this.selectedMode = command;
      this.initializeModel(modeConfigs[command]);
      return command;
    }
    return message;
  }

  async sendMessage(message: string): Promise<void> {
    this.isLoading = true;
    const processedMessage = this.configureMode(message.trim());

    if (!processedMessage.startsWith('Hi!')) {
      this.addMessage({
        text: processedMessage,
        sender: 'user',
        timestamp: new Date(),
      });
    } else {
      this.hiFlag = true;
    }

    const botMessage: ChatMessage = {
      text: '',
      sender: 'bot',
      timestamp: new Date(),
      isTyping: true,
    };
    this.addMessage(botMessage);

    try {
      const result = await this.chatSession.sendMessageStream(processedMessage);
      await this.processStreamResponse(processedMessage,result.stream, botMessage);
      this.isLoading = false;
    } catch (error) {
      this.handleError(botMessage, error);
      this.isLoading = false;
    }
  }

  private async processStreamResponse(
    processedMessage: string,
    stream: AsyncIterable<any>,
    botMessage: ChatMessage
  ): Promise<void> {
    let accumulatedText = '';

    // Отображаем текст в реальном времени из стрима
    for await (const chunk of stream) {
      accumulatedText += chunk.text();
      botMessage.text = await marked(accumulatedText);
      this.updateChatHistory();
    }
    //сохранение истории на сервер
    this.saveHistory(processedMessage, accumulatedText);
    // После завершения стрима проверяем весь текст на JSON с company_domains
    const companyJson = this.extractAndProcessJSON(accumulatedText);
    if (companyJson) {
      accumulatedText =
        accumulatedText.replace(companyJson, '') + '\n[[COMPANY_CARD]]';
      botMessage.text = await marked(accumulatedText);
      botMessage.companies = this.extractCompaniesFromJson(companyJson);
    }

    botMessage.isTyping = false;

    //сохранение истории локально
    this.updateChatHistory();
  }

  private saveHistory(question:string,response:string):void{
    this.history.addHistory({body:{message:question,response:response}}).subscribe();
  }

  private extractAndProcessJSON(responseText: string): string | undefined {
    const extractJSON = (str: string): string | null => {
      let firstOpen = str.indexOf('{');
      let firstClose, candidate;

      while (firstOpen !== -1) {
        firstClose = str.lastIndexOf('}');

        if (firstClose <= firstOpen) {
          return null;
        }

        while (firstClose > firstOpen) {
          candidate = str.substring(firstOpen, firstClose + 1);

          try {
            const parsed = JSON.parse(candidate);
            // Проверяем наличие нужного ключа в JSON
            if (parsed && parsed.company_domains && Array.isArray(parsed.company_domains)) {
              return candidate;
            }
          } catch (e) {
            // Игнорируем ошибки парсинга и продолжаем
          }

          firstClose = str.lastIndexOf('}', firstClose - 1);
        }

        firstOpen = str.indexOf('{', firstOpen + 1);
      }

      return null;
    };

    try {
      // Сначала проверяем markdown-блок
      const markdownJsonPattern = /```json\s*([\s\S]*?)\s*```/g;
      let match;
      while ((match = markdownJsonPattern.exec(responseText)) !== null) {
        const candidate = match[1];
        if (this.validateAndReturnJson(candidate)) {
          return candidate;
        }
      }

      // Затем используем надежный подход с парсингом
      return extractJSON(responseText) || undefined;
    } catch (error) {
      console.error('Error in JSON extraction:', error);
      return undefined;
    }
  }


  private validateAndReturnJson(jsonString: string): string | undefined {
    try {
      const jsonData = JSON.parse(jsonString);

      if (Array.isArray(jsonData)) {
        // Если верхний уровень — массив, проверим объекты на наличие company_domains
        for (const item of jsonData) {
          if (item.company_domains && Array.isArray(item.company_domains)) {
            return jsonString;
          }
        }
      } else if (jsonData.company_domains && Array.isArray(jsonData.company_domains)) {
        // Проверяем наличие ключа в объекте
        return jsonString;
      }

      console.warn('JSON does not contain valid company_domains');
      return undefined;
    } catch (error) {
      console.error('Error parsing JSON:', error);
      return undefined;
    }
  }

  private extractCompaniesFromJson(jsonString: string): CompanyDomain[] {
    try {
      const jsonData: CompanyDomainsData = JSON.parse(jsonString);
      if (jsonData.company_domains && Array.isArray(jsonData.company_domains)) {
        return jsonData.company_domains as CompanyDomain[];
      }
      return [];
    } catch (error) {
      console.error('Error parsing company JSON:', error);
      return [];
    }
  }

  private handleError(botMessage: ChatMessage, error: unknown): void {
    console.error('Error:', error);
    botMessage.isTyping = false;
    botMessage.text = '<em>Something went wrong. Please try again.</em>';
    this.updateChatHistory();
  }

  clearChatHistory(): void {
    this.chatHistory = [];
    this.updateChatHistory();
  }

  async refreshPrompts() {
    const genAI = new GoogleGenerativeAI(this.apiKey);
    const model = genAI.getGenerativeModel({
      model: "gemini-2.0-flash",
     // model: "gemini-2.5-pro-exp-03-25",
      systemInstruction: "You are a professional Prompt Engineer, the best in the world. The user will send an instruction for an AI. Your task: thoroughly analyze this instruction and create 10 concise, original, and engaging prompts (max 100 characters each). Always include real companies or famous individuals, but strictly avoid using any companies or people mentioned in the instruction provided by the user. Your prompts should be clever, witty, and unusual. Only Software products, Any Companies" + ' Use topic: '+ this.selectedMode,
    });

    const generationConfig:GenerationConfig = {
      temperature: 1,
      topP: 0.95,
      topK: 64,
      maxOutputTokens: 65536,
      responseMimeType: "application/json",
      responseSchema: {
        type: SchemaType.OBJECT,
        properties: {
          prompts: {
            type: SchemaType.ARRAY,
            items: {
              type: SchemaType.STRING
            }
          }
        },
        required: ["prompts"]
      },
    };

    try {
      const chatSession = model.startChat({
        generationConfig,
        history: [],
      });

      const result = await chatSession.sendMessage(this.model.systemInstruction.parts.map(part => part.text).join(""));
      const responseText = result.response.text();
      const responseJson = JSON.parse(responseText);

      return responseJson.prompts || [];
    } catch (error) {
      console.error("Error generating prompts:", error);
      return [];
    }
  }
}
